/**
 * React hook for authentication state and actions
 */
import { useCallback, useEffect, useState } from 'react';
import { autoRefreshToken, isAuthenticated, login, logout } from '../services/auth.service';
import type { AuthError, LoginRequest } from '../types/auth.types';

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => boolean;
}

/**
 * Hook for managing authentication state
 */
export const useAuth = (): UseAuthReturn => {
  const [isAuthenticatedState, setIsAuthenticatedState] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const authenticated = isAuthenticated();
        setIsAuthenticatedState(authenticated);

        // Auto-refresh token if needed
        if (authenticated) {
          await autoRefreshToken();
        }
      } catch {
        setIsAuthenticatedState(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login handler
  const handleLogin = useCallback(async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await login(credentials);
      setIsAuthenticatedState(true);
      // Don't redirect here - let the calling component handle navigation
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
      setIsAuthenticatedState(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout handler
  const handleLogout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await logout();
      setIsAuthenticatedState(false);
      globalThis.location.href = '/';
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
      // Still navigate to root even if logout fails
      setIsAuthenticatedState(false);
      globalThis.location.href = '/';
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check auth status
  const checkAuth = useCallback(() => {
    const authenticated = isAuthenticated();
    setIsAuthenticatedState(authenticated);
    return authenticated;
  }, []);

  return {
    isAuthenticated: isAuthenticatedState,
    isLoading,
    error,
    login: handleLogin,
    logout: handleLogout,
    checkAuth,
  };
};
